import { useTranslation } from "react-i18next";

import type { StateCounts, StateProportion } from "../reportTypes";
import { ASSESSED_ORDER, stateColourVar, stateLabels } from "../stateMeta";
import { pct } from "../format";

/**
 * A 100%-stacked horizontal bar showing the risk / change / protective split for
 * one phase. Dependency-free SVG/CSS (no charting library — see REPORTING.md
 * "Charting approach"); colours come from the --state-* tokens so admin colour
 * overrides flow through automatically.
 *
 * Accessibility: the bar carries a full text readout as its aria-label, and the
 * accompanying <StateReadout> renders the same numbers as on-paper text, so the
 * data never depends on the segment colours alone.
 */
type Props = {
  proportion: StateProportion;
  /** Phase label for the accessible summary, e.g. "At entry". */
  phaseLabel: string;
  /** Minimum segment width (%) before the inline % label is shown. */
  minLabelPct?: number;
};

export function StateBar({ proportion, phaseLabel, minLabelPct = 12 }: Props) {
  const { t } = useTranslation();

  const summary = ASSESSED_ORDER.map(
    (k) => `${pct(proportion[k])} ${stateLabels(t, k).en}`,
  ).join(", ");

  return (
    <div
      className="rpt-bar"
      role="img"
      aria-label={`${phaseLabel}: ${summary}`}
    >
      {ASSESSED_ORDER.map((k) => {
        const w = proportion[k] * 100;
        if (w <= 0) return null;
        return (
          <span
            key={k}
            className="rpt-bar__seg"
            style={{ width: `${w}%`, background: stateColourVar(k) }}
          >
            {w >= minLabelPct && (
              <span className="rpt-bar__seg-label">{pct(proportion[k])}</span>
            )}
          </span>
        );
      })}
    </div>
  );
}

/**
 * On-paper numeric readout matching a StateBar — state glyph swatch + label + %.
 * Always legible regardless of the bar colours.
 */
export function StateReadout({
  proportion,
  counts,
}: {
  proportion: StateProportion;
  counts?: StateCounts;
}) {
  const { t } = useTranslation();
  return (
    <ul className="rpt-readout">
      {ASSESSED_ORDER.map((k) => (
        <li key={k} className="rpt-readout__item">
          <span
            className="rpt-readout__swatch"
            style={{ background: stateColourVar(k) }}
            aria-hidden="true"
          />
          <span className="rpt-readout__pct">{pct(proportion[k])}</span>
          <span className="rpt-readout__label">{stateLabels(t, k).en}</span>
          {counts && <span className="rpt-readout__count">({counts[k]})</span>}
        </li>
      ))}
    </ul>
  );
}

export default StateBar;
